import { CircularProgress } from '@mui/material';
import { Button, IconButton } from 'design-system';
import { MdDownload } from 'react-icons/md';
import { useIsPrint } from 'hooks/useIsPrint';
import { isHeadless } from 'utils/headless';
import { ButtonColor } from 'generated/graphql';

export enum IconPosition {
  Right = 'Right',
  Left = 'Left',
}

export enum ButtonSizes {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

const DownloadButton = ({
  onDownload,
  isLoading = false,
  buttonName = 'Download',
  isSmall = false,
  size,
  hasIcon = false,
  iconOnly = false,
  iconPosition = IconPosition.Right,
  color = ButtonColor.Secondary,
  variant = 'contained',
  noPadding = false,
}: {
  onDownload: () => void;
  isLoading?: boolean;
  buttonName?: string;
  isSmall?: boolean;
  size?: ButtonSizes;
  hasIcon?: boolean;
  iconOnly?: boolean;
  iconPosition?: IconPosition;
  color?: ButtonColor | 'inherit';
  variant?: 'contained' | 'outlined';
  noPadding?: boolean;
}) => {
  const isRobot = isHeadless();
  const isPrint = useIsPrint();

  const validColors = ['inherit', 'primary', 'secondary', 'success', 'error', 'info', 'warning'];
  const buttonColor = validColors.includes(color.toLowerCase()) ? color : 'secondary';

  if (isRobot || isPrint) return null;

  if (iconOnly) {
    return (
      <IconButton color={buttonColor.toLowerCase() as any} onClick={onDownload} disabled={isLoading}>
        {isLoading ? <CircularProgress size={20} /> : <MdDownload />}
      </IconButton>
    );
  }

  return (
    <Button
      disabled={isLoading}
      color={buttonColor.toLowerCase() as any}
      variant={variant}
      size={size ? size : isSmall ? 'small' : 'large'}
      onClick={onDownload}
      startIcon={iconPosition === IconPosition.Left && hasIcon ? <MdDownload /> : null}
      endIcon={iconPosition === IconPosition.Right && hasIcon ? <MdDownload /> : null}
      sx={(theme) => ({
        paddingX: isSmall && !noPadding ? theme.spacing(2) : theme.spacing(1),
        '&:hover': {
          backgroundColor: buttonColor === 'inherit' ? theme.palette.common.white : undefined,
          color: buttonColor === 'inherit' ? theme.palette.primary.main : undefined,
        },
      })}
    >
      {buttonName}
      {isLoading && (
        <CircularProgress
          sx={{
            position: 'absolute',
            top: 'calc(50% - 12px)',
            left: 'calc(50% - 12px)',
          }}
          size={24}
          color="secondary"
        />
      )}
    </Button>
  );
};

export default DownloadButton;
